<template>
  <v-card outlined class="mb-3">
    <v-card-subtitle class="d-flex align-center pb-2">
      <v-icon small color="primary" class="mr-2">mdi-format-list-checks</v-icon>
      <span class="font-weight-bold primary--text">{{ $t("food_package.rules_title") }}</span>
    </v-card-subtitle>
    <v-card-text>
      <div v-if="!editedItem.id" class="grey--text text--darken-1">
        {{ $t("food_package.rules_need_save") }}
      </div>
      <div v-else-if="!groups.length" class="grey--text text--darken-1">
        {{ $t("food_package.rules_no_groups") }}
      </div>
      <template v-else>
        <p class="text-body-2 grey--text text--darken-1 mb-3">
          {{ $t("food_package.rules_hint") }}
        </p>
        <v-progress-linear v-if="loading" indeterminate color="primary" class="mb-2" />
        <v-simple-table dense class="package-rules-table">
          <thead>
            <tr>
              <th class="text-left">{{ $t("food_package.group") }}</th>
              <th class="text-left">{{ $t("food_package.min_select") }}</th>
              <th class="text-left">{{ $t("food_package.max_select") }}</th>
              <th class="text-left">{{ $t("food_package.sort") }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in rows" :key="row.category_id">
              <td>
                {{ toLatin(row.name) }}
                <div v-if="!rowValid(row)" class="error--text text-caption">
                  {{ $t("food_package.min_gt_max") }}
                </div>
              </td>
              <td style="width: 120px">
                <text-field v-model="row.min_select" type="number" hide-details />
              </td>
              <td style="width: 120px">
                <text-field v-model="row.max_select" type="number" hide-details />
              </td>
              <td style="width: 120px">
                <text-field v-model="row.sort" type="number" min="0" hide-details />
              </td>
            </tr>
          </tbody>
        </v-simple-table>
        <div class="d-flex justify-end mt-3">
          <primary-button :disabled="!canSave" :loading="saving" @click="save">
            {{ $t("food_package.save_rules") }}
          </primary-button>
        </div>
      </template>
    </v-card-text>
  </v-card>
</template>

<script>
import mealService from "@/services/meal";
import hasSnackbar from "@/mixins/hasSnackbar";

const isEmpty = (value) => value === null || value === undefined || value === "";
const toInt = (value) => (isEmpty(value) ? null : parseInt(value, 10));

export default {
  name: "FoodPackageRules",
  mixins: [hasSnackbar],
  props: {
    editedItem: {
      type: Object,
      required: true,
    },
  },
  data: () => ({
    loading: false,
    saving: false,
    /** Pravila koja je server vratio, po category_id. */
    savedRules: {},
    /** Redovi tabele: jedan po grupi izbora. */
    rows: [],
  }),
  computed: {
    /** Grupe iz kojih paket ima izbore: kategorije izabranih priloga. */
    groups() {
      const map = new Map();
      (this.editedItem.prilog || []).forEach((dish) => {
        const category = dish.category;
        if (category?.id && !map.has(category.id)) {
          map.set(category.id, { category_id: category.id, name: category.name || "" });
        }
      });
      return [...map.values()];
    },
    canSave() {
      return this.rows.every(this.rowValid);
    },
  },
  watch: {
    "editedItem.id": {
      immediate: true,
      handler(id) {
        this.savedRules = {};
        this.rows = [];
        if (id) {
          this.load();
        }
      },
    },
    groups() {
      this.buildRows();
    },
  },
  methods: {
    /** Red za svaku grupu; izmene korisnika za grupe koje već postoje ostaju. */
    buildRows() {
      const current = new Map(this.rows.map((row) => [row.category_id, row]));
      this.rows = this.groups.map((group, index) => {
        const existing = current.get(group.category_id);
        if (existing) {
          return { ...existing, name: group.name };
        }
        const saved = this.savedRules[group.category_id] || {};
        return {
          ...group,
          min_select: saved.min_select ?? "",
          max_select: saved.max_select ?? "",
          sort: saved.sort ?? index + 1,
        };
      });
    },
    rowValid(row) {
      const min = toInt(row.min_select);
      const max = toInt(row.max_select);
      return min === null || max === null || min <= max;
    },
    errorMessage(error, fallback) {
      return error?.response?.data?.message || fallback;
    },
    async load() {
      this.loading = true;
      try {
        const { data } = await mealService.getPackageRules(this.editedItem.id);
        const rules = Array.isArray(data) ? data : data?.rules || data?.data || [];
        this.savedRules = rules.reduce((map, rule) => {
          map[rule.category_id] = rule;
          return map;
        }, {});
        this.rows = [];
        this.buildRows();
      } catch (error) {
        this.showErrorSnackbar(
          this.errorMessage(error, this.$t("food_package.rules_load_error"))
        );
      } finally {
        this.loading = false;
      }
    },
    /** Šalje celu listu; grupa bez popunjenog reda koristi pravilo kategorije. */
    async save() {
      const rules = this.rows
        .filter((row) => !isEmpty(row.min_select) || !isEmpty(row.max_select))
        .map((row, index) => ({
          category_id: row.category_id,
          min_select: toInt(row.min_select) ?? 0,
          max_select: toInt(row.max_select),
          sort: toInt(row.sort) ?? index + 1,
        }));
      this.saving = true;
      try {
        await mealService.savePackageRules(this.editedItem.id, rules);
        this.showSuccessSnackbar(this.$t("food_package.rules_saved"));
        await this.load();
      } catch (error) {
        this.showErrorSnackbar(
          this.errorMessage(error, this.$t("food_package.rules_save_error"))
        );
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.package-rules-table tbody td {
  padding-top: 8px !important;
  padding-bottom: 8px !important;
  height: auto !important;
}
</style>
